import { arrayMove } from '@dnd-kit/sortable';
import type { Medication } from '../../../../../../../lib/transferObjects/Medication';
import type { MedSection } from '../../../../hook/types';
import { IMPORTANT_PATIENT_INFORMATION } from '../../../../constants';

export const SECTION_ID_PREFIX = 'section::';
export const MEDICATION_ID_PREFIX = 'medication::';
export const MEDICATION_CONTAINER_ID_PREFIX = 'medication-container::';

export const getSectionSortableId = (section: MedSection, index: number) =>
  `${SECTION_ID_PREFIX}${section.medSectionBreak}__${index}`;

export const getMedicationSortableId = (
  section: MedSection,
  sectionIndex: number,
  medication: Partial<Medication>,
  medicationIndex: number,
) =>
  `${MEDICATION_ID_PREFIX}${section.medSectionBreak}__${sectionIndex}__${String(
    medication?.medId ?? medication?.medName ?? medicationIndex,
  )}`;

export const getMedicationContainerId = (section: MedSection, index: number) =>
  `${MEDICATION_CONTAINER_ID_PREFIX}${getSectionSortableId(section, index)}`;

export const canDragSection = (section: MedSection) =>
  section.medSectionBreak !== IMPORTANT_PATIENT_INFORMATION;

export const normalizeSectionAndMedicationOrder = (sections: MedSection[]) =>
  sections.map((section, sectionIndex) => ({
    ...section,
    order: sectionIndex + 1,
    medications: (Array.isArray(section.medications)
      ? section.medications
      : []
    ).map((medication, medicationIndex) => ({
      ...medication,
      order: medicationIndex + 1,
    })),
  }));

export const isSectionSortableId = (id: string) =>
  id.startsWith(SECTION_ID_PREFIX);

export const isMedicationSortableId = (id: string) =>
  id.startsWith(MEDICATION_ID_PREFIX);

export const findSectionIndexBySortableId = (
  sections: MedSection[],
  sortableId: string,
) =>
  sections.findIndex(
    (section, index) => getSectionSortableId(section, index) === sortableId,
  );

export const findMedicationLocationBySortableId = (
  sections: MedSection[],
  sortableId: string,
) => {
  for (
    let sectionIndex = 0;
    sectionIndex < sections.length;
    sectionIndex += 1
  ) {
    const medications = Array.isArray(sections[sectionIndex]?.medications)
      ? sections[sectionIndex].medications
      : [];

    for (
      let medicationIndex = 0;
      medicationIndex < medications.length;
      medicationIndex += 1
    ) {
      if (
        getMedicationSortableId(
          sections[sectionIndex],
          sectionIndex,
          medications[medicationIndex],
          medicationIndex,
        ) === sortableId
      ) {
        return { sectionIndex, medicationIndex };
      }
    }
  }

  return null;
};

export const findMedicationDropLocation = (
  sections: MedSection[],
  sortableId: string,
) => {
  const medicationLocation = findMedicationLocationBySortableId(
    sections,
    sortableId,
  );
  if (medicationLocation) return medicationLocation;

  const sectionIndex = findSectionIndexBySortableId(sections, sortableId);
  if (sectionIndex >= 0) {
    return {
      sectionIndex,
      medicationIndex: Array.isArray(sections[sectionIndex]?.medications)
        ? sections[sectionIndex].medications.length
        : 0,
    };
  }

  const containerIndex = sections.findIndex(
    (section, index) => getMedicationContainerId(section, index) === sortableId,
  );
  if (containerIndex >= 0) {
    return {
      sectionIndex: containerIndex,
      medicationIndex: Array.isArray(sections[containerIndex]?.medications)
        ? sections[containerIndex].medications.length
        : 0,
    };
  }

  return null;
};

export const findMedicationBySortableId = (
  sections: MedSection[],
  sortableId: string,
) => {
  const location = findMedicationLocationBySortableId(sections, sortableId);
  if (!location) return null;

  const section = sections[location.sectionIndex];
  const medication = Array.isArray(section?.medications)
    ? section.medications[location.medicationIndex]
    : null;

  if (!medication) return null;

  return {
    sectionIndex: location.sectionIndex,
    medicationIndex: location.medicationIndex,
    medication,
  };
};

export const reorderDraggableSections = (
  sections: MedSection[],
  sectionSortableItems: string[],
  activeId: string,
  overId: string,
) => {
  const oldIndex = sectionSortableItems.indexOf(activeId);
  const newIndex = sectionSortableItems.indexOf(overId);

  if (oldIndex < 0 || newIndex < 0) return null;

  const draggableSections = sections.filter(canDragSection);
  const reorderedDraggableSections = arrayMove(
    draggableSections,
    oldIndex,
    newIndex,
  );

  let draggableIndex = 0;
  const reordered = sections.map((section) => {
    if (!canDragSection(section)) return section;
    const nextSection = reorderedDraggableSections[draggableIndex];
    draggableIndex += 1;
    return nextSection;
  });
  return normalizeSectionAndMedicationOrder(reordered);
};

export const moveMedicationBySortableIds = (
  sections: MedSection[],
  activeId: string,
  overId: string,
) => {
  const activeLocation = findMedicationLocationBySortableId(sections, activeId);
  const overLocation = findMedicationDropLocation(sections, overId);

  if (!activeLocation || !overLocation) return null;

  const activeSection = sections[activeLocation.sectionIndex];
  const overSection = sections[overLocation.sectionIndex];
  if (!activeSection || !overSection) return null;

  if (!canDragSection(activeSection) || !canDragSection(overSection)) {
    return null;
  }

  const nextSections = sections.map((section) => ({
    ...section,
    medications: Array.isArray(section.medications)
      ? [...section.medications]
      : [],
  }));

  const [movedMedication] = nextSections[
    activeLocation.sectionIndex
  ].medications.splice(activeLocation.medicationIndex, 1);

  if (!movedMedication) return null;

  const sameSection = activeLocation.sectionIndex === overLocation.sectionIndex;
  const nextMedicationIndex =
    sameSection && activeLocation.medicationIndex < overLocation.medicationIndex
      ? overLocation.medicationIndex - 1
      : overLocation.medicationIndex;

  nextSections[overLocation.sectionIndex].medications.splice(
    nextMedicationIndex,
    0,
    movedMedication,
  );

  return normalizeSectionAndMedicationOrder(nextSections);
};
